import { Stack } from '@mui/system';
import { Typography } from '@mui/material';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ComboboxOption } from '@common/ui/Combobox/Combobox';
import { useNavigate } from 'react-router-dom';
import { useCreateUserAccessRequest, useListPermissions, useListResourceTypes } from '@apono/client-app-sdk';
import { toast } from 'react-toastify';
import routes from '@routes';
import { FormState, initialFormState } from '@MyRequests/helpers';
import { AccessUnitSelector } from '@NewRequest/components/AccessUnitSelector';
import { LoadingButton } from '@mui/lab';
import { ResourceTypeFilterOption } from '@NewRequest/components/ResourceTypeFilter';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { CustomField } from '@common/organisms/RequestCustomFields';
import { RequestFields } from '@common/organisms/RequestFields';
import { createAccessRequestClientModel } from '@NewRequest/utils/helpers';
import RequestErrorBanner from '@NewRequest/components/RequestErrorBanner';

export function ResourcesRequestForm() {
  const { track } = useAnalyticsContext();

  const [formState, setFormState] = useState<FormState>(initialFormState);

  const navigate = useNavigate();
  const { createUserAccessRequest, isLoading, isSuccess, isError, error } = useCreateUserAccessRequest();
  const noAccessUnitSelected = formState.selectedAccessUnit?.length === 0;
  const [isDryRunErrors, setIsDryRunErrors] = useState(false);

  const handleFormChange = useCallback(
    (field: keyof FormState, value: ComboboxOption | ComboboxOption[] | string | number | CustomField | null) => {
      setFormState({ ...formState, [field]: value });
    },
    [formState],
  );

  useEffect(() => {
    if (isSuccess) {
      toast.success('Access request created successfully');
      track(AnalyticsEvents.ResourcesRequestSent);
      navigate(routes.OpenRequests.path);
    }
    if (isError) {
      track(AnalyticsEvents.ResourcesRequestFailed);
    }
  }, [isSuccess, isError, navigate, track]);

  const formDisabled = noAccessUnitSelected || isDryRunErrors;

  const handleRequest = async () => {
    if (formDisabled) return;
    createUserAccessRequest({ createAccessRequestClientModel: createAccessRequestClientModel(formState) });
  };

  return (
    <Stack spacing={2}>
      {isError && <RequestErrorBanner error={error} />}
      <AccessUnitSelectorWrapper
        formState={formState}
        updateFormState={(val) => handleFormChange('selectedAccessUnit', val)}
      />
      <RequestFields
        withDuration
        formState={formState}
        updateFormState={handleFormChange}
        onDryRunErrorsChange={(isDryRunErrors) => setIsDryRunErrors(isDryRunErrors)}
      />
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <LoadingButton
          data-trigger={AnalyticsEvents.ResourcesRequestSendClicked}
          variant="contained"
          color="primary"
          onClick={handleRequest}
          loading={isLoading}
          disabled={formDisabled}
          type="submit"
        >
          Request
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

function AccessUnitSelectorWrapper({
  formState,
  updateFormState,
}: {
  formState: FormState;
  updateFormState: (value: ComboboxOption | ComboboxOption[] | string | null) => void;
}) {
  const { data: listResourceTypes, isFetched: isResourceTypeFetched } = useListResourceTypes({ limit: 10000 });
  const resourceTypeOptions: ResourceTypeFilterOption[] | undefined = useMemo(() => {
    return listResourceTypes?.pages[0].data.map((rt) => ({
      icon: rt.icons.svg,
      label: rt.name,
      value: rt.id,
    }));
  }, [listResourceTypes]);

  const { data: permissions, isFetched: isPermissionFetched } = useListPermissions({ limit: 10000 });
  const permissionOptions: ComboboxOption[] | undefined = useMemo(() => {
    return permissions?.pages[0].data.map((p) => ({
      label: p.name,
      value: p.id,
    }));
  }, [permissions]);

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="caption" component="span">
        Resources
      </Typography>
      <AccessUnitSelector
        value={formState.selectedAccessUnit}
        onChange={updateFormState}
        resourceTypeOptions={resourceTypeOptions}
        isResourceTypeFetched={isResourceTypeFetched}
        permissionOptions={permissionOptions}
        isPermissionFetched={isPermissionFetched}
      />
    </Stack>
  );
}
